import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/feature/Header';
import Footer from '../components/feature/Footer';
import { useProducts } from '../hooks/useProducts';
import { apiFetch } from '../lib/apiClient';
import type { OrderResource } from '../types/api';
import { countries } from '../mocks/countries';
import { buildEcommerceItem, trackEcommerceEvent, trackEvent } from '../lib/analytics';

const LOCALE_MAP: Record<string, string> = {
  en: 'en', de: 'de', at: 'de', hu: 'hu',
  pl: 'pl', nl: 'nl', fr: 'fr', it: 'it',
  cz: 'cs', sk: 'sk', sl: 'sl', ro: 'ro', bg: 'bg',
};

function mapLocale(lang: string): string {
  return LOCALE_MAP[lang] ?? LOCALE_MAP[lang.split('-')[0]] ?? 'en';
}

const steps = [
  { id: 1, key: 'period' },
  { id: 2, key: 'vehicle' },
  { id: 3, key: 'email' },
  { id: 4, key: 'review' },
];

const VT_UI: Record<string, { key: string; icon: string }> = {
  'car': { key: 'car', icon: 'ri-car-line' },
  'motorcycle': { key: 'motorcycle', icon: 'ri-motorbike-line' },
  'trailer': { key: 'trailer', icon: 'ri-truck-line' },
  'van / minibus (d2)': { key: 'van', icon: 'ri-bus-line' },
  'cng / biomethane': { key: 'cng', icon: 'ri-gas-station-line' },
  'plug-in hybrid (eco)': { key: 'phev', icon: 'ri-car-line' },
};

const regCountries = [
  { code: 'DE', name: 'Germany' },
  { code: 'PL', name: 'Poland' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'AT', name: 'Austria' },
  { code: 'HU', name: 'Hungary' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'RO', name: 'Romania' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'DK', name: 'Denmark' },
  { code: 'ES', name: 'Spain' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'SE', name: 'Sweden' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'US', name: 'USA' },
];

const DRAFT_KEY = 'checkoutDraft';

type CheckoutDraft = {
  step?: number;
  vehicleType?: string;
  regCountry?: string;
  plate?: string;
  vin?: string;
  period?: string;
  startDate?: string;
  email?: string;
  countryCode?: string;
  entryProductId?: string | null;
};

export default function CheckoutPage() {
  const [params] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { data: products, loading: productsLoading } = useProducts();

  const periodParam = params.get('period') || '';
  const priceParam = parseFloat(params.get('price') || '0');

  // Restore an in-progress checkout (e.g. after a page reload) from sessionStorage.
  // The orderId link-resume flow below takes priority, so skip the draft entirely then.
  const [initialDraft] = useState<CheckoutDraft | null>(() => {
    if (params.get('orderId')) return null;
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      const draft = JSON.parse(raw) as CheckoutDraft;
      const urlCountry = (params.get('country') || '').toLowerCase();
      if (urlCountry && draft.countryCode && urlCountry !== draft.countryCode) return null;

      const urlProductId = params.get('productId');
      if (urlProductId && draft.entryProductId && urlProductId !== draft.entryProductId) return null;
      return draft;
    } catch {
      return null;
    }
  });

  const [countryCode, setCountryCode] = useState(initialDraft?.countryCode ?? (params.get('country') || '').toLowerCase());
  const countryData = products?.find(c => c.code === countryCode.toUpperCase()) ?? null;
  const requiresVin = countryData?.fields.some(f => f.name === 'vin') ?? false;

  const [step, setStep] = useState(initialDraft?.step ?? 1);
  const [vehicleType, setVehicleType] = useState(initialDraft?.vehicleType ?? 'car');
  const [regCountry, setRegCountry] = useState(initialDraft?.regCountry ?? 'PL');
  const [plate, setPlate] = useState(initialDraft?.plate ?? '');
  const [vin, setVin] = useState(initialDraft?.vin ?? '');
  const [period, setPeriod] = useState(initialDraft?.period ?? periodParam);
  const [startDate, setStartDate] = useState(initialDraft?.startDate ?? new Date().toISOString().split('T')[0]);
  const [email, setEmail] = useState(initialDraft?.email ?? '');
  const [emailTouch, setEmailTouch] = useState(false);

  const [plateError, setPlateError] = useState('');
  const [vinError, setVinError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [dateError, setDateError] = useState('');
  const [periodError, setPeriodError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [returnToReview, setReturnToReview] = useState(false);
  const [pendingOrder, setPendingOrder] = useState<OrderResource | null>(null);

  const regCountryName = regCountries.find((c) => c.code === regCountry)?.name || regCountry;
  const today = new Date().toISOString().split('T')[0];


  // Scroll back to the top whenever the checkout step changes.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // GA4: checkout_step_view (custom) — fires every time a step is shown, including back/edit navigation.
  useEffect(() => {
    const stepMeta = steps.find(s => s.id === step);
    trackEvent('checkout_step_view', {
      step_number: step,
      step_name: stepMeta?.key ?? String(step),
    });
  }, [step]);

  const beginCheckoutFiredRef = useRef(false);

  // GA4: begin_checkout — fires once, only when the flow's very first step is the one on screen at load.
  useEffect(() => {
    if (beginCheckoutFiredRef.current) return;
    if (step !== steps[0].id) return;
    if (!countryData || !periodParam) return;
    beginCheckoutFiredRef.current = true;
    const item = buildEcommerceItem({
      countryCode: countryData.code,
      countryName: countryData.name,
      period: periodParam,
      price: priceParam,
      currency: countryData.currency,
      productId: params.get('productId'),
    });
    trackEcommerceEvent('begin_checkout', [item], {
      value: priceParam,
      currency: countryData.currency,
    });
  }, [step, countryData, periodParam, priceParam, params]);

  useEffect(() => {
    if (plate) validatePlate(plate);
  }, [regCountry]);

  // Persist the in-progress checkout so a reload resumes on the same step.
  // Skipped while resuming via the orderId link flow, which has its own restore path.
  useEffect(() => {
    if (params.get('orderId')) return;
    const draft: CheckoutDraft = {
      step, vehicleType, regCountry, plate, vin, period, startDate, email, countryCode,
      entryProductId: params.get('productId'),
    };
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [step, vehicleType, regCountry, plate, vin, period, startDate, email, countryCode, params]);

  useEffect(() => {
    const uuid = params.get('orderId');
    if (!uuid) return;
    let cancelled = false;
    apiFetch<{ data: OrderResource }>(`/v1/orders/${uuid}`)
      .then(({ data: order }) => {
        if (cancelled) return;
        if (order.payment_status === 'paid') return;
        setPlate(order.license_plate);
        setEmail(order.email);
        setRegCountry(order.country_registration);
        setStartDate(order.start_date.split('T')[0]);
        if (order.vin) setVin(order.vin);
        if (order.product) {
          setVehicleType(order.product.vehicle_type);
          setPeriod(order.product.period);
        }
        setPendingOrder(order);
        setStep(4);
        setReturnToReview(true);
      })
      .catch(() => { /* leave the form as-is on error */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!products || !pendingOrder?.product) return;
    const orderCountry = pendingOrder.product.country.toLowerCase();
    const matched = products.find(c =>
      c.code.toLowerCase() === orderCountry || c.name.toLowerCase() === orderCountry
    );
    if (matched) setCountryCode(matched.code.toLowerCase());
  }, [products, pendingOrder]);

  const currentOptions = useMemo(() => {
    return countryData?.vehicle_types.find(vt => vt.name === vehicleType)?.prices ?? [];
  }, [countryData, vehicleType]);

  const availablePeriods = currentOptions.map(o => o.period);

  const selectedPrice = useMemo(() => {
    if (!period) return priceParam;
    const opt = currentOptions.find(o => o.period === period);
    return opt ? parseFloat(opt.price) : priceParam;
  }, [currentOptions, period, priceParam]);

  const productId = currentOptions.find(o => o.period === period)?.id ?? null;

  const currency = countryData?.currency ?? 'EUR';

  const trackFormError = (field: string, errorType: 'invalid_format' | 'server_rejected') => {
    trackEvent('form_error', {
      step_name: steps.find(s => s.id === step)?.key ?? String(step),
      field,
      error_type: errorType,
    });
  };

  const validatePlate = (value: string) => {
    const cleaned = value.trim().replace(/[\s-]/g, '').toUpperCase();
    if (!cleaned) {
      setPlateError(t('checkout.error.plateRequired'));
      trackFormError('plate', 'invalid_format');
      return false;
    }
    if (cleaned.length < 2 || cleaned.length > 10 || !/^[A-Z0-9]+$/.test(cleaned)) {
      setPlateError(t('checkout.error.plateInvalid'));
      trackFormError('plate', 'invalid_format');
      return false;
    }
    setPlateError('');
    return true;
  };

  const validateEmail = (value: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value.trim()) {
      setEmailError(t('checkout.error.emailRequired'));
      trackFormError('email', 'invalid_format');
      return false;
    }
    if (!re.test(value)) {
      setEmailError(t('checkout.error.emailInvalid'));
      trackFormError('email', 'invalid_format');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validateVin = (value: string) => {
    const cleaned = value.trim().replace(/\s+/g, '').toUpperCase();
    if (!cleaned) {
      setVinError(t('checkout.error.vinRequired'));
      trackFormError('vin', 'invalid_format');
      return false;
    }
    if (cleaned.length !== 17) {
      setVinError(t('checkout.error.vinLength'));
      trackFormError('vin', 'invalid_format');
      return false;
    }
    if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(cleaned)) {
      setVinError(t('checkout.error.vinInvalid'));
      trackFormError('vin', 'invalid_format');
      return false;
    }
    setVinError('');
    return true;
  };

  const validateDate = (value: string) => {
    if (!value) {
      setDateError(t('checkout.error.dateRequired'));
      trackFormError('start_date', 'invalid_format');
      return false;
    }
    const selected = new Date(value);
    const todayDate = new Date(today);
    if (isNaN(selected.getTime())) {
      setDateError(t('checkout.error.dateInvalid'));
      trackFormError('start_date', 'invalid_format');
      return false;
    }
    const currentYear = todayDate.getFullYear();
    if (selected.getFullYear() < currentYear || selected.getFullYear() > currentYear + 2) {
      setDateError(t('checkout.error.dateInvalid'));
      trackFormError('start_date', 'invalid_format');
      return false;
    }
    if (selected < todayDate) {
      setDateError(t('checkout.error.datePast'));
      trackFormError('start_date', 'invalid_format');
      return false;
    }
    setDateError('');
    return true;
  };

  const canProceed = () => {
    if (step === 1) {
      const periodOk = !!period;
      if (!periodOk) {
        setPeriodError(t('checkout.error.periodRequired'));
        trackFormError('period', 'invalid_format');
      } else {
        setPeriodError('');
      }
      const dateOk = validateDate(startDate);
      return periodOk && dateOk;
    }
    if (step === 2) {
      const plateOk = validatePlate(plate);
      const vinOk = requiresVin ? validateVin(vin) : true;
      return plateOk && vinOk;
    }
    return true;
  };

  const validateEmailRemote = async (value: string) => {
    try {
      await apiFetch<{ valid: boolean }>('/v1/orders/validate', {
        method: 'POST',
        body: JSON.stringify({ email: value }),
      });
      setEmailError('');
      return true;
    } catch (e) {
      const err = e as { status?: number; body?: { message?: string; errors?: Record<string, string[]> } };
      if (err.status === 422) {
        const fieldError = err.body?.errors?.email?.[0];
        setEmailError(fieldError || err.body?.message || t('checkout.error.emailInvalid'));
        trackFormError('email', 'server_rejected');
        return false;
      }
      return true;
    }
  };

  const advanceStep = () => {
    if (step === 2 && !currentOptions.some(o => o.period === period)) {
      setPeriodError(t('checkout.error.periodRequired'));
      setStep(1);
      return;
    }
    if (returnToReview) {
      setReturnToReview(false);
      setStep(4);
    } else if (step < 4) {
      setStep(step + 1);
    }
  };

  const nextStep = async () => {
    if (step === 3) {
      if (!validateEmail(email)) return;
      trackEvent('email_check_started', {});
      setEmailChecking(true);
      const ok = await validateEmailRemote(email);
      setEmailChecking(false);
      if (!ok) return;
      trackEvent('checkout_email_submitted', {});
      advanceStep();
      return;
    }
    if (!canProceed()) return;
    if (step === steps.find(s => s.key === 'vehicle')?.id) {
      trackEvent('checkout_vehicle_submitted', {
        vehicle_type: vehicleType,
        registration_country: regCountry,
      });
    }
    if (step === steps.find(s => s.key === 'period')?.id) {
      const offsetDays = Math.round(
        (new Date(startDate).getTime() - new Date(today).getTime()) / (1000 * 60 * 60 * 24)
      );
      trackEvent('checkout_period_submitted', {
        period,
        start_date_offset_days: offsetDays,
        period_changed: period !== periodParam,
      });
    }
    advanceStep();
  };

  const prevStep = () => {
    if (step > 1) {
      trackEvent('checkout_back_click', {
        from_step: steps.find(s => s.id === step)?.key ?? String(step),
        to_step: steps.find(s => s.id === step - 1)?.key ?? String(step - 1),
      });
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    if (countryData) {
      trackEcommerceEvent('add_payment_info', [buildEcommerceItem({
        countryCode: countryData.code,
        countryName: countryData.name,
        period,
        price: selectedPrice,
        currency,
        productId,
      })], {
        value: selectedPrice,
        currency,
        payment_type: 'card',
      });
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        product_id: productId,
        country_registration: regCountry,
        license_plate: plate.trim().replace(/[\s-]/g, '').toUpperCase(),
        email,
        locale: mapLocale(i18n.language),
        start_date: `${startDate}T00:00:00Z`,
        ...(requiresVin ? { vin: vin.trim().toUpperCase() } : {}),
      };
      const result = pendingOrder
        ? await apiFetch<{ data: OrderResource }>(`/v1/orders/${pendingOrder.id}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
          })
        : await apiFetch<{ data: OrderResource }>('/v1/orders', {
            method: 'POST',
            body: JSON.stringify(payload),
          });
      if (result.data.payment_url) {
        sessionStorage.removeItem(DRAFT_KEY);
        window.location.href = result.data.payment_url;
        return;
      }
      setSubmitError('Payment link is unavailable. Please contact support.');
    } catch (e) {
      const err = e as { status?: number; body?: { message?: string; errors?: Record<string, string[]> } };
      if (err.status === 422 && err.body) {
        const firstFieldError = err.body.errors
          ? Object.values(err.body.errors)[0]?.[0]
          : null;
        setSubmitError(firstFieldError || err.body.message || 'Please check the entered details.');
      } else {
        setSubmitError('Something went wrong. Please try again in a moment.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const vehicleTypes = countryData?.vehicle_types ?? [];
  const countryName = countryData?.name ?? countryCode.toUpperCase();
  const CC2KEY: Record<string, string> = {
    at: 'austria', hu: 'hungary', ch: 'switzerland', cz: 'czechRepublic',
    sk: 'slovakia', si: 'slovenia', ro: 'romania', bg: 'bulgaria',
  };
  const localizedCountry = CC2KEY[countryCode] ? t(`countries.${CC2KEY[countryCode]}.name`) : countryName;
  const periodLabel = (p: string) => t(`countryPage.period.${p.toLowerCase().replace(/\s+/g, '')}`, { defaultValue: p });
  const vehicleLabel = VT_UI[vehicleType] ? t(`checkout.vt.${VT_UI[vehicleType].key}.label`) : vehicleType;
  const countryPath = countries.find(c => c.code.toLowerCase() === countryCode)?.path ?? '/';

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      <Header />
      <main className="flex-1">
        <div className="border-b border-border bg-surface">
          <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
            <Link
              to={countryPath}
              onClick={() => trackEvent('checkout_exit', { step_number: step })}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted transition hover:text-ink"
            >
              <i className="ri-arrow-left-line text-[14px]" />
              {t('checkout.backToLanding')}
            </Link>
            <span className="text-[12px] font-semibold uppercase tracking-wider text-muted">
              {t('checkout.stepOf', { current: step, total: 4 })}
            </span>
          </div>

          {/* Progress bar */}
          <div className="mx-auto max-w-3xl px-5 pb-4">
            <div className="flex items-center gap-2">
              {steps.map((s, idx) => (
                <div key={s.id} className="flex flex-1 items-center gap-2">
                  <div
                    className={`h-1 flex-1 rounded-full transition ${
                      s.id <= step ? 'bg-primary' : 'bg-border'
                    }`}
                  />
                  {idx < steps.length - 1 && (
                    <div className="hidden sm:block w-2" />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 hidden sm:grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
              {steps.map((s) => (
                <div
                  key={s.id}
                  className={`text-[12px] font-medium transition ${
                    s.id === step
                      ? 'text-ink font-semibold'
                      : s.id < step
                        ? 'text-primary'
                        : 'text-muted'
                  }`}
                >
                  {s.id}. {t(`checkout.step.${s.key}`)}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="mx-auto max-w-3xl px-5 py-10">
          {/* STEP 1 — Period */}
          {step === 1 && (
            <div>
              <h1 className="text-[24px] font-bold tracking-tight text-ink">{t('checkout.step2.title')}</h1>
              <p className="mt-1 text-[15px] text-muted">
                {t('checkout.step2.subtitle', { country: localizedCountry })}
              </p>

              <div className="mt-6 rounded-xl border border-border bg-surface p-6 md:p-7 space-y-6">
                {/* Period selection */}
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                    {t('checkout.validityPeriod')}
                  </label>
                  {availablePeriods.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {currentOptions.map((opt) => {
                        const active = period === opt.period;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => { setPeriod(opt.period); setPeriodError(''); }}
                            className={`flex items-center justify-between rounded-lg border px-4 py-3.5 text-left transition cursor-pointer ${
                              active
                                ? 'border-primary bg-primary-soft'
                                : periodError
                                  ? 'border-red-400 bg-white hover:border-primary'
                                  : 'border-border bg-white hover:border-primary'
                            }`}
                          >
                            <span
                              className={`text-[15px] font-semibold ${
                                active ? 'text-ink' : 'text-ink-soft'
                              }`}
                            >
                              {periodLabel(opt.period)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-[14px] text-muted">No periods available.</p>
                  )}
                  {periodError && (
                    <p className="mt-2 text-[13px] text-red-500">{periodError}</p>
                  )}
                </div>

                {/* Start date */}
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                    {t('checkout.startDate')}
                  </label>
                  <input
                    type="date"
                    min={today}
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      if (dateError) validateDate(e.target.value);
                    }}
                    onBlur={() => startDate && validateDate(startDate)}
                    className={`block w-full min-w-0 min-h-[52px] appearance-none rounded-lg border bg-white px-4 py-3.5 text-[15px] text-ink outline-none transition focus:border-primary ${dateError ? 'border-red-400' : 'border-border'}`}
                  />
                  {dateError && (
                    <p className="mt-2 text-[13px] text-red-500">{dateError}</p>
                  )}
                  <p className="mt-2 text-[13px] text-muted">
                    {t('checkout.startDateHint')}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={productsLoading || !period || !startDate || !!dateError}
                  className="inline-flex items-center justify-center rounded-lg bg-primary px-7 py-3 text-[15px] font-semibold text-white transition hover:bg-primary-hover cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {returnToReview ? t('checkout.saveAndReview') : t('checkout.continue')}
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — Vehicle */}
          {step === 2 && (
            <div>
              <h1 className="text-[24px] font-bold tracking-tight text-ink">{t('checkout.yourVehicle')}</h1>
              <p className="mt-1 text-[15px] text-muted">
                {t('checkout.yourVehicleSubtitle')}
              </p>

              <div className="mt-6 rounded-xl border border-border bg-surface p-6 md:p-7 space-y-6">
                {/* Vehicle type */}
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                    {t('checkout.vehicleType')}
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(vehicleTypes.length > 0 ? vehicleTypes : [{ id: 0, name: 'car', prices: [] }]).map((vt) => {
                        const ui = VT_UI[vt.name] ?? { key: '', icon: 'ri-car-line' };
                        const vtLabel = ui.key ? t(`checkout.vt.${ui.key}.label`) : vt.name;
                        const vtDesc = ui.key ? t(`checkout.vt.${ui.key}.desc`) : '';
                        return (
                          <button
                            key={vt.id}
                            type="button"
                            onClick={() => {
                              const currentDays = currentOptions.find(o => o.period === period)?.period_days;
                              const matched = vt.prices.find(o => o.period_days === currentDays);
                              setVehicleType(vt.name);
                              setPeriod(matched?.period ?? '');
                            }}
                            className={`flex items-center gap-4 rounded-lg border px-4 py-3.5 text-left transition cursor-pointer ${
                              vehicleType === vt.name
                                ? 'border-primary bg-primary-soft'
                                : 'border-border bg-white hover:border-primary'
                            }`}
                          >
                            <div
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                                vehicleType === vt.name ? 'bg-primary text-white' : 'bg-bg text-muted'
                              }`}
                            >
                              <i className={`${ui.icon} text-[18px]`} />
                            </div>
                            <div>
                              <div
                                className={`text-[15px] font-semibold ${
                                  vehicleType === vt.name ? 'text-ink' : 'text-ink-soft'
                                }`}
                              >
                                {vtLabel}
                              </div>
                              <div className="text-[13px] text-muted">{vtDesc}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  <p className="mt-3 text-[13px] text-muted">
                    {t('checkout.heavyTrucksNote')}
                  </p>
                </div>

                {/* Country of registration */}
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                    {t('checkout.countryOfRegistration')}
                  </label>
                  <div className="relative">
                    <select
                      value={regCountry}
                      onChange={(e) => setRegCountry(e.target.value)}
                      className="w-full appearance-none rounded-lg border border-border bg-white px-4 py-3.5 text-[15px] text-ink outline-none transition focus:border-primary cursor-pointer pr-10"
                    >
                      {regCountries.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name} ({c.code})
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none text-[18px]" />
                  </div>
                  <p className="mt-2 text-[13px] text-muted">
                    {t('checkout.countryOfRegistrationHint')}
                  </p>
                </div>

                {/* License plate */}
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                    {t('checkout.licensePlate')}
                  </label>
                  <div className="flex overflow-hidden rounded-lg border border-border focus-within:border-primary transition bg-white">
                    <div className="flex shrink-0 items-center bg-primary px-4">
                      <span className="text-[14px] font-bold text-white">{regCountry}</span>
                    </div>
                    <input
                      type="text"
                      value={plate}
                      onChange={(e) => {
                        setPlate(e.target.value.toUpperCase().replace(/[\s-]/g, ''));
                        if (plateError) validatePlate(e.target.value);
                      }}
                      onBlur={() => validatePlate(plate)}
                      placeholder="KJUYGUY7"
                      className="w-full bg-white px-4 py-3.5 text-[15px] font-semibold uppercase text-ink outline-none placeholder:font-normal placeholder:text-muted/60"
                    />
                  </div>
                  {plateError && (
                    <p className="mt-2 text-[13px] text-red-500">{plateError}</p>
                  )}
                  <p className="mt-2 text-[13px] text-muted">
                    {t('checkout.licensePlateHint')}
                  </p>
                </div>

                {/* VIN — countries that require it */}
                {requiresVin && (
                  <div>
                    <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                      VIN (Vehicle Identification Number)
                    </label>
                    <input
                      type="text"
                      value={vin}
                      onChange={(e) => {
                        setVin(e.target.value.toUpperCase());
                        if (vinError) validateVin(e.target.value);
                      }}
                      onBlur={() => validateVin(vin)}
                      placeholder="WWZZZ1KZBW000000"
                      className={`w-full rounded-lg border bg-white px-4 py-3.5 text-[15px] font-semibold uppercase text-ink outline-none placeholder:font-normal placeholder:text-muted/60 transition ${
                        vinError ? 'border-red-400' : 'border-border focus:border-primary'
                      }`}
                    />
                    {vinError && (
                      <p className="mt-2 text-[13px] text-red-500">{vinError}</p>
                    )}
                    <p className="mt-2 text-[13px] text-muted">
                      17 characters from your vehicle registration document.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-5 py-3 text-[14px] font-medium text-ink transition hover:border-primary cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line text-[14px]" />
                  {t('checkout.back')}
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={!regCountry || !plate || !!plateError || (requiresVin && (!vin || !!vinError))}
                  className="inline-flex items-center justify-center rounded-lg bg-primary px-7 py-3 text-[15px] font-semibold text-white transition hover:bg-primary-hover cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {returnToReview ? t('checkout.saveAndReview') : t('checkout.continue')}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — Email */}
          {step === 3 && (
            <div>
              <h1 className="text-[24px] font-bold tracking-tight text-ink">{t('checkout.step3.title')}</h1>
              <p className="mt-1 text-[15px] text-muted">
                {t('checkout.step3.subtitle')}
              </p>

              <div className="mt-6 rounded-xl border border-border bg-surface p-6 md:p-7 space-y-6">
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                    {t('checkout.emailAddress')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEmail(value);
                      if (emailTouch) validateEmail(value);
                    }}
                    onBlur={() => {
                      setEmailTouch(true);
                      validateEmail(email);
                    }}
                    placeholder={t('checkout.emailPlaceholder')}
                    className={`w-full rounded-lg border bg-white px-4 py-3.5 text-[15px] text-ink outline-none transition placeholder:text-muted/60 focus:border-primary ${
                      emailError ? 'border-red-400' : 'border-border'
                    }`}
                  />
                  {emailError && (
                    <p className="mt-2 text-[13px] text-red-500">{emailError}</p>
                  )}
                  <p className="mt-2 text-[13px] text-muted">
                    {t('checkout.emailHint')}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-5 py-3 text-[14px] font-medium text-ink transition hover:border-primary cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line text-[14px]" />
                  {t('checkout.back')}
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={!email || !!emailError || emailChecking}
                  className="inline-flex items-center justify-center rounded-lg bg-primary px-7 py-3 text-[15px] font-semibold text-white transition hover:bg-primary-hover cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {emailChecking ? t('checkout.checking', { defaultValue: 'Checking…' }) : returnToReview ? t('checkout.saveAndReview') : t('checkout.continue')}
                </button>
              </div>
            </div>
          )}

          {/* STEP 4 — Review */}
          {step === 4 && (
            <div>
              <h1 className="text-[24px] font-bold tracking-tight text-ink">{t('checkout.step4.title')}</h1>
              <p className="mt-1 text-[15px] text-muted">
                {t('checkout.step4.subtitle')}
              </p>

              <div className="mt-6 rounded-xl border border-border bg-surface p-6 md:p-7 space-y-5">
                {/* Summary rows */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[13px] text-muted">{t('checkout.review.vehicle')}</div>
                    <div className="text-[15px] font-semibold text-ink">{vehicleLabel}</div>
                    <div className="text-[13px] text-ink-soft">{regCountry} — {plate.trim().replace(/\s+/g, ' ').toUpperCase()}</div>
                    {requiresVin && vin && (
                      <div className="text-[13px] text-ink-soft">VIN: {vin.toUpperCase()}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => { trackEvent('review_edit_click', { section: 'vehicle' }); setReturnToReview(true); setStep(2); }}
                    disabled={submitting}
                    className="text-[13px] font-medium text-primary hover:underline cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
                  >
                    {t('checkout.edit')}
                  </button>
                </div>

                <div className="h-px bg-border" />

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[13px] text-muted">{t('checkout.review.evignette')}</div>
                    <div className="text-[15px] font-semibold text-ink">{localizedCountry}</div>
                    <div className="text-[13px] text-ink-soft">
                      {t('checkout.review.periodFrom', { period: periodLabel(period), date: startDate })}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { trackEvent('review_edit_click', { section: 'vignette' }); setReturnToReview(true); setStep(1); }}
                    disabled={submitting}
                    className="text-[13px] font-medium text-primary hover:underline cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
                  >
                    {t('checkout.edit')}
                  </button>
                </div>

                <div className="h-px bg-border" />

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[13px] text-muted">{t('checkout.step3.title')}</div>
                    <div className="text-[15px] font-semibold text-ink">{email}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { trackEvent('review_edit_click', { section: 'email' }); setReturnToReview(true); setStep(3); }}
                    disabled={submitting}
                    className="text-[13px] font-medium text-primary hover:underline cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
                  >
                    {t('checkout.edit')}
                  </button>
                </div>

                <div className="h-px bg-border" />

                {/* Pricing */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-[14px]">
                    <span className="text-muted">{t('checkout.review.vignette')}</span>
                    {productsLoading ? (
                      <span className="h-4 w-16 rounded bg-border animate-pulse" />
                    ) : (
                      <span className="font-semibold text-ink">
                        {selectedPrice.toFixed(2)} {currency}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between border-t border-border pt-3">
                    <span className="text-[15px] font-semibold text-ink">{t('checkout.review.total')}</span>
                    {productsLoading ? (
                      <span className="h-6 w-20 rounded bg-border animate-pulse" />
                    ) : (
                      <span className="text-[20px] font-bold text-primary">
                        {selectedPrice.toFixed(2)} {currency}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || productsLoading}
                  className="mt-2 w-full rounded-lg bg-primary px-6 py-3.5 text-[15px] font-semibold text-white transition hover:bg-primary-hover cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Processing…' : t('checkout.proceedToPayment')}
                </button>

                {submitError && (
                  <p className="text-center text-[13px] text-red-500">{submitError}</p>
                )}

                <p className="text-center text-[12px] text-muted">
                  {t('checkout.paymentHint')}
                </p>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-5 py-3 text-[14px] font-medium text-ink transition hover:border-primary cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line text-[14px]" />
                  {t('checkout.back')}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

